import api, { toUrlEncoded, toUriEncoded } from './baseApi';
import {
    CancellationReasonDto,
    CardUpdateResult,
    Customer,
    InvoiceList,
    PauseReasonDto,
    PaymentMethod,
    Price,
    Subscription,
    SubscriptionStatusSummary,
    SubscriptionStatusUpdate
} from './typings/billing';

export const getPaymentMethod = async (store: string): Promise<PaymentMethod> => {
    const response = await api.get(`Users/Billing/PaymentMethods/${toUriEncoded(store)}`);
    return response.data;
};

export const getDefaultPaymentMethod = async (store: string): Promise<PaymentMethod> => {
    const response = await api.get(`Users/Billing/PaymentMethods/${toUriEncoded(store)}/default`);
    return response.data;
};

export const updateCard = async (store: string, token: string): Promise<CardUpdateResult> => {
    const response = await api.post(
        `Users/Billing/PaymentMethods/${toUriEncoded(store)}`,
        toUrlEncoded({
            Token: token
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }
    );
    return response.data;
};

export const updateCreditCardForShopifyUser = async (token: string): Promise<void> => {
    await api.post(
        `Users/Billing/PaymentMethods/UpdateCreditCardForShopifyUser`,
        toUrlEncoded({
            CreditCardTokenId: token
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }
    );
};

export const getInvoices = async (
    store: string,
    limit?: number,
    startingAfter?: string,
    endingBefore?: string
): Promise<InvoiceList> => {
    const response = await api.get(`Users/Billing/Invoices/${toUriEncoded(store)}`, {
        params: {
            limit: limit,
            startingAfter: startingAfter,
            endingBefore: endingBefore
        }
    });
    return response.data;
};

export const getCustomer = async (store: string): Promise<Customer> => {
    const response = await api.get(`Users/Billing/Customer/${toUriEncoded(store)}`);
    return response.data;
};

export const getSubscription = async (store: string): Promise<Subscription> => {
    const response = await api.get(`Users/Billing/Subscriptions/${toUriEncoded(store)}`);
    return response.data;
};

export const getSubscriptions = async (): Promise<Subscription[]> => {
    const response = await api.get(`Users/Billing/Subscriptions`);
    return response.data;
};

export const getSubscriptionsStatus = async (): Promise<SubscriptionStatusSummary[]> => {
    const response = await api.get(`Users/Billing/StoresStatus`);
    return response.data;
};

export const updateSubscription = async (
    store: string,
    password: string,
    status: SubscriptionStatusUpdate,
    cardTokenId?: string,
    cancelReasons?: CancellationReasonDto[],
    pauseReasons?: PauseReasonDto[]
): Promise<{ Id: string; ConfirmationUrl: string }> => {
    const response = await api.put(
        `Users/Billing/Subscriptions/${toUriEncoded(store)}`,
        toUrlEncoded({
            Password: password,
            Status: status,
            CancellationReasons: cancelReasons,
            PauseReasons: pauseReasons,
            CardTokenId: cardTokenId
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }
    );
    return response.data;
};

export const getLastPrice = async (store: string): Promise<Price | null> => {
    const response = await api.get(`Users/Billing/Prices/Last/${toUriEncoded(store)}`);
    return response.data;
};

export const setDefaultPaymentMethod = async (store: string): Promise<PaymentMethod> => {
    const response = await api.post(`Users/Billing/PaymentMethods/${toUriEncoded(store)}/setDefault`);
    return response.data;
};

export const createCheckoutSession = async (username: string, storeType: string): Promise<string> => {
    const response = await api.post(`Stripe/CreateCheckoutSession/${username}`, { storeType });
    return response.data;
};
